import { ThemeEvents } from '@theme/events';

let lockCount = 0;

/**
 * Interactive product video player: muted teaser + fullscreen overlay.
 * @extends {HTMLElement}
 */
class ProductVideoPlayer extends HTMLElement {
  /** @type {boolean} */
  #isOpen = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #indicatorTimeout = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Element | null} */
  #previouslyFocused = null;
  /** @type {HTMLElement | null} */
  #overlay = null;
  /** @type {HTMLElement | null} */
  #overlayParent = null;
  /** @type {boolean} */
  #isSeeking = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#overlay = this.querySelector('.pvp__overlay');
    const signal = this.#abortController.signal;
    const overlay = this.#overlay;

    this.querySelector('[data-action="open"]')?.addEventListener('click', () => this.#open(), { signal });
    this.querySelector('[data-action="teaser-mute"]')?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        event.preventDefault();
        const teaser = this.#getTeaserVideo();
        if (!teaser) return;
        if (teaser.muted) {
          this.#enableSound(teaser);
          teaser.play().catch(() => {});
        } else {
          teaser.muted = true;
        }
        this.#updateTeaserMuteButton();
      },
      { signal }
    );
    overlay?.querySelectorAll('[data-action="close"]').forEach((el) => {
      el.addEventListener('click', () => this.#close(), { signal });
    });

    overlay?.querySelector('[data-action="toggle-play"]')?.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('button, a, product-form-component, quick-add-component, .pvp__product-card')) {
          return;
        }
        this.#togglePlay();
      },
      { signal }
    );

    overlay?.querySelector('[data-action="mute"]')?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        const player = this.#getPlayerVideo();
        if (player) {
          if (player.muted) {
            this.#enableSound(player);
          } else {
            player.muted = true;
          }
          this.#updateMuteButton();
        }
      },
      { signal }
    );

    overlay?.querySelector('[data-action="share"]')?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.#share();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#isOpen) {
          this.#close();
        }
      },
      { signal }
    );

    const player = this.#getPlayerVideo();
    if (player) {
      player.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          if (player.muted) {
            this.#enableSound(player);
            this.#updateMuteButton();
            if (player.paused) player.play().catch(() => {});
            return;
          }
          this.#togglePlay();
        },
        { signal }
      );
      player.addEventListener('timeupdate', () => this.#updateProgress(), { signal });
      player.addEventListener('loadedmetadata', () => this.#updateProgress(), { signal });
    }

    this.querySelectorAll('.pvp__teaser-video').forEach((teaser) => {
      teaser.addEventListener(
        'pause',
        () => {
          if (!this.#isOpen) {
            teaser.play().catch(() => {});
          }
        },
        { signal }
      );
    });

    const onCartUpdate = (event) => {
      if (!this.#isOpen || event.detail?.data?.didError) return;
      const target = /** @type {Node} */ (event.target);
      if (this.contains(target) || this.#overlay?.contains(target)) {
        this.#close();
      }
    };
    this.addEventListener(ThemeEvents.cartUpdate, onCartUpdate, { signal });
    overlay?.addEventListener(ThemeEvents.cartUpdate, onCartUpdate, { signal });

    this.#bindSeekControls(overlay, signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#isOpen) {
      this.#unlockBody();
      this.#restoreOverlay();
    }
  }

  #open() {
    const overlay = this.#overlay;
    const player = this.#getPlayerVideo();
    const teaser = this.#getTeaserVideo();
    if (!overlay || !player) return;

    if (overlay.parentElement !== document.body) {
      this.#overlayParent = overlay.parentElement;
      document.body.appendChild(overlay);
    }

    this.#isOpen = true;
    this.#previouslyFocused = document.activeElement;
    overlay.hidden = false;
    void overlay.offsetHeight;
    overlay.classList.add('is-open');

    if (!player.getAttribute('src') && player.dataset.src) {
      player.src = player.dataset.src;
    }

    const resumeAt = teaser?.currentTime || 0;

    if (teaser) {
      teaser.pause();
      teaser.muted = true;
      this.#updateTeaserMuteButton();
    }

    if (resumeAt > 0) {
      const applyResume = () => {
        if (Number.isFinite(player.duration) && resumeAt < player.duration) {
          player.currentTime = resumeAt;
        }
      };

      if (player.readyState >= 1) {
        applyResume();
      } else {
        player.addEventListener('loadedmetadata', applyResume, { once: true });
      }
    }

    player.defaultMuted = false;
    player.muted = false;
    player.volume = 1;
    player.removeAttribute('muted');
    this.#updateMuteButton();
    this.#lockBody();

    const playPromise = player.play();
    overlay.querySelector('.pvp__close')?.focus();
    if (!playPromise) return;

    const playMuted = () => {
      player.muted = true;
      this.#updateMuteButton();
      player.play().catch(() => {});
    };

    playPromise.catch((error) => {
      if (error?.name === 'NotAllowedError') {
        playMuted();
        return;
      }

      this.#enableSound(player);
      player.play().catch((retryError) => {
        if (retryError?.name === 'NotAllowedError') playMuted();
      });
    });
  }

  /** @param {HTMLVideoElement} video */
  #enableSound(video) {
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;
    video.removeAttribute('muted');
  }

  #close() {
    const overlay = this.#overlay;
    const player = this.#getPlayerVideo();
    const teaser = this.#getTeaserVideo();
    if (!overlay || !this.#isOpen) return;

    this.#isOpen = false;
    overlay.classList.remove('is-open');

    let closed = false;
    const finishClose = () => {
      if (closed) return;
      closed = true;
      overlay.hidden = true;
      this.#restoreOverlay();
    };

    overlay.addEventListener('transitionend', finishClose, { once: true });
    setTimeout(finishClose, 450);

    player?.pause();
    if (teaser) {
      teaser.play().catch(() => {});
    }

    this.#unlockBody();
    /** @type {HTMLElement} */ (this.#previouslyFocused)?.focus?.();
  }

  #togglePlay() {
    const player = this.#getPlayerVideo();
    if (!player) return;

    if (player.paused) {
      player.play();
      this.#showIndicator('pause');
    } else {
      player.pause();
      this.#showIndicator('play');
    }
  }

  /** @param {'play' | 'pause'} type */
  #showIndicator(type) {
    const indicator = this.#overlay?.querySelector('.pvp__indicator');
    if (!indicator) return;

    indicator.querySelector('[data-indicator="play"]')?.toggleAttribute('hidden', type !== 'play');
    indicator.querySelector('[data-indicator="pause"]')?.toggleAttribute('hidden', type !== 'pause');
    indicator.classList.add('is-visible');

    if (this.#indicatorTimeout) clearTimeout(this.#indicatorTimeout);
    this.#indicatorTimeout = setTimeout(() => {
      indicator.classList.remove('is-visible');
    }, 600);
  }

  #updateProgress() {
    const overlay = this.#overlay;
    const player = this.#getPlayerVideo();
    const bar = overlay?.querySelector('.pvp__progress');
    const current = overlay?.querySelector('[data-current-time]');
    const duration = overlay?.querySelector('[data-duration]');
    if (!player || !bar) return;

    const total = player.duration || 0;
    const now = player.currentTime || 0;
    const ratio = total ? now / total : 0;

    bar.style.setProperty('--pvp-progress', `${ratio * 100}%`);
    bar.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));

    if (current) current.textContent = this.#formatTime(now);
    if (duration) duration.textContent = this.#formatTime(total);
  }

  #updateMuteButton() {
    const player = this.#getPlayerVideo();
    const button = this.#overlay?.querySelector('[data-action="mute"]');
    if (!player || !button) return;

    button.querySelector('[data-icon="muted"]')?.toggleAttribute('hidden', !player.muted);
    button.querySelector('[data-icon="unmuted"]')?.toggleAttribute('hidden', player.muted);
    button.setAttribute('aria-label', player.muted ? button.dataset.labelUnmute : button.dataset.labelMute);
  }

  #updateTeaserMuteButton() {
    const teaser = this.#getTeaserVideo();
    const button = this.querySelector('[data-action="teaser-mute"]');
    if (!teaser || !button) return;

    button.querySelector('[data-icon="muted"]')?.toggleAttribute('hidden', !teaser.muted);
    button.querySelector('[data-icon="unmuted"]')?.toggleAttribute('hidden', teaser.muted);
    button.setAttribute('aria-label', teaser.muted ? button.dataset.labelUnmute : button.dataset.labelMute);
  }

  async #share() {
    const title = this.getAttribute('share-title') || document.title;
    const url = this.getAttribute('share-url') || window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* user cancelled or API unavailable */
    }
  }

  /** @param {number} seconds */
  #formatTime(seconds) {
    const safeSeconds = Number.isFinite(seconds) ? seconds : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  #getTeaserVideo() {
    const videos = this.querySelectorAll('.pvp__teaser-video');
    return Array.from(videos).find((video) => video.offsetParent !== null) || videos[0];
  }

  #getPlayerVideo() {
    return this.#overlay?.querySelector('.pvp__video');
  }

  #restoreOverlay() {
    const overlay = this.#overlay;
    const parent = this.#overlayParent;
    if (overlay && parent && overlay.parentElement === document.body) {
      parent.appendChild(overlay);
      this.#overlayParent = null;
    }
  }

  /** @param {number} clientX @param {HTMLElement} hitArea */
  #seekFromPointer(clientX, hitArea) {
    const player = this.#getPlayerVideo();
    const bar = hitArea?.querySelector('.pvp__progress');
    if (!player || !player.duration || !hitArea) return;

    const rect = hitArea.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    player.currentTime = ratio * player.duration;

    if (bar) {
      bar.style.setProperty('--pvp-progress', `${ratio * 100}%`);
      bar.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }

    this.#updateProgress();
  }

  /** @param {HTMLElement | null} overlay @param {AbortSignal} signal */
  #bindSeekControls(overlay, signal) {
    overlay?.querySelectorAll('[data-action="seek"]').forEach((hitArea) => {
      const bar = hitArea.querySelector('.pvp__progress');

      const endSeek = () => {
        this.#isSeeking = false;
        bar?.classList.remove('is-seeking');
      };

      hitArea.addEventListener(
        'pointerdown',
        (event) => {
          event.stopPropagation();
          event.preventDefault();
          hitArea.setPointerCapture(event.pointerId);
          this.#isSeeking = true;
          bar?.classList.add('is-seeking');
          this.#seekFromPointer(event.clientX, hitArea);
        },
        { signal }
      );

      hitArea.addEventListener(
        'pointermove',
        (event) => {
          if (!this.#isSeeking) return;
          this.#seekFromPointer(event.clientX, hitArea);
        },
        { signal }
      );

      hitArea.addEventListener(
        'pointerup',
        (event) => {
          if (hitArea.hasPointerCapture(event.pointerId)) {
            hitArea.releasePointerCapture(event.pointerId);
          }
          endSeek();
        },
        { signal }
      );

      hitArea.addEventListener('pointercancel', endSeek, { signal });
    });
  }

  #lockBody() {
    lockCount += 1;
    document.documentElement.classList.add('lock');
  }

  #unlockBody() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.documentElement.classList.remove('lock');
    }
  }
}

if (!customElements.get('product-video-player')) {
  customElements.define('product-video-player', ProductVideoPlayer);
}
